import { OwnPropertyQuestion } from '@hb/domain/steps/extra-asset';
import { FieldSubject } from '@hb/domain/field-subject';
import { money } from '@hb/builder';
import { ExtraAssetStoreService } from '@hb/modules/extra-asset/extra-asset-store.service';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';

import { ExtraAsset } from '@ap/ap-interface-apix-hypotheken-berekening';
import { Money } from '@ap/interface-bspc-core';

@Injectable({
    providedIn: 'root'
})
export class OwnPropertyPartStoreService extends AbstractStoreInjectOption<OwnPropertyQuestion> implements OnDestroy {
    protected readonly store = inject(ExtraAssetStoreService);
    protected readonly lastQuestion = OwnPropertyQuestion.SUMMARY;

    private readonly ownPropertySubject: FieldSubject<boolean>;
    private readonly sellingPriceSubject: FieldSubject<Money>;
    private readonly mortgageAmountSubject: FieldSubject<Money>;

    constructor() {
        super();
        this.init();

        const initialValues = this.getInitialValues();
        this.ownPropertySubject = new FieldSubject<boolean>(initialValues?.own_property);
        this.sellingPriceSubject = new FieldSubject<Money>(initialValues?.selling_price);
        this.mortgageAmountSubject = new FieldSubject<Money>(initialValues?.mortgage_amount);

        this.subscriptions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.activeQuestionSubject.unsubscribe();
        this.ownPropertySubject.unsubscribe();
        this.sellingPriceSubject.unsubscribe();
        this.mortgageAmountSubject.unsubscribe();
    }

    update(): void {
        this.store.setOwnProperty(this.ownPropertySubject.value);
        this.store.setSellingPrice(this.sellingPriceSubject.value);
        this.store.setMortgageAmount(this.mortgageAmountSubject.value);
    }

    isActiveQuestion(question: OwnPropertyQuestion): boolean {
        switch (question) {
            case OwnPropertyQuestion.OWN_PROPERTY_BUTTON:
                return true;
            case OwnPropertyQuestion.SELLING_PRICE_INPUT:
            case OwnPropertyQuestion.MORTGAGE_AMOUNT_INPUT:
                return this.ownPropertySubject.value;
            case OwnPropertyQuestion.SUMMARY:
            default:
                return false;
        }
    }

    getOwnProperty(): boolean {
        return this.ownPropertySubject.value;
    }

    setOwnProperty(ownProperty: boolean): void {
        this.ownPropertySubject.next(ownProperty);
    }

    getSellingPrice(): Money {
        return this.sellingPriceSubject.value;
    }

    setSellingPrice(sellingPrice: string): void {
        this.sellingPriceSubject.next(money(sellingPrice));
    }

    getMortgageAmount(): Money {
        return this.mortgageAmountSubject.value;
    }

    setMortgageAmount(mortgageAmount: string): void {
        this.mortgageAmountSubject.next(money(mortgageAmount));
    }

    getInitialValues(): ExtraAsset {
        return this.store.getExtraAssetState();
    }

    private subscriptions(): void {
        this.ownPropertySubject.asObservable().subscribe((ownProperty: boolean) => {
            if (!ownProperty) {
                this.sellingPriceSubject.next(null);
                this.mortgageAmountSubject.next(null);
                this.update();
            } else {
                this.update();
            }
            this.nextQuestion();
        });
        this.sellingPriceSubject.asObservable().subscribe(() => this.update());
        this.mortgageAmountSubject.asObservable().subscribe(() => this.update());
    }
}
